import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Page } from '../library/Page';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PageHeader } from '../library/PageHeader';
import { PricingPublishDialog } from '../PricingPublishDialog';
import { ClientDataBranch } from '../../constants/ClientDataBranch';
import { I18nKeys } from '../../constants/I18nKeys';
import { PricingPublishResultDialog } from '../PricingPublishResultDialog';
import { getClientIdFromClientSupplier } from '../../utils/clientIdUtils';
import { unknownGroup } from '../../constants/Group';
import { PricingCategories } from './PricingCategories';
import { useClientUpdatePricingRepo } from '../../hooks/useClientUpdatePricingRepo';
import { notifyOfDataChange } from '../../utils/pricingUtils';
import { LocalStorage } from '../../constants/LocalStorage';
import { PricingClientUpdatePreviewDialog } from '../PricingClientUpdatePreviewDialog';
import { filterClientUpdateCategories } from '../../utils/pricingClientUpdateUtils';
import { ComponentPricingItems } from './ComponentPricingItems';
import { PricingComponentEditDialog } from '../PricingComponentEditDialog';

export const ComponentPrices: React.FC<{ clientId: string }> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { selectedCategoryKey: componentCategoryKey } = useAppSelector((state) => state.pricing.component);
  const searchTerm = useAppSelector((state) => state.search.searchTerm);
  const clientId = useAppSelector((state) =>
    getClientIdFromClientSupplier(state?.viewer?.selectedTabId || state?.viewer?.selectedClientId || ''),
  );

  const { group: { groupId } = unknownGroup } = useAppSelector((state) => state?.currentUser);

  const {
    clientUpdateBranchMergeCommit,
    clientUpdateCategoriesList = [],
    isLoadingClientUpdateCategoriesList,
  } = useClientUpdatePricingRepo({
    useClientUpdateBranchMergeCommit: true,
    useClientUpdateCategories: true,
  });

  useEffect(() => {
    notifyOfDataChange({
      groupId,
      clientId,
      latestCommit: clientUpdateBranchMergeCommit,
      key: LocalStorage.LastPricingSizeBasedMergeCommit,
      dispatch,
      t,
    });
  }, [clientUpdateBranchMergeCommit, dispatch, t, groupId, clientId]);

  const filteredComponentCategories = filterClientUpdateCategories(searchTerm, clientUpdateCategoriesList, t);

  return (
    <Page sx={{ width: '100%', ...(!componentCategoryKey ? { minHeight: '100%' } : { height: '100%' }) }}>
      {!componentCategoryKey && (
        <PageHeader
          id="components-header"
          header="Components"
          count={filteredComponentCategories?.length}
          loading={isLoadingClientUpdateCategoriesList}
          countLabel="category"
        />
      )}
      {!componentCategoryKey && (
        <Box display="flex" flex={1} position="relative">
          <PricingCategories />
        </Box>
      )}
      {componentCategoryKey && (
        <Box sx={{ width: '100%', height: '100%' }}>
          <ComponentPricingItems />
        </Box>
      )}
      <PricingComponentEditDialog />
      <PricingClientUpdatePreviewDialog />
      <PricingPublishDialog
        pricingBranch={ClientDataBranch.Pricing}
        title={I18nKeys.PricingComponentPublishDialogTitle}
        content={I18nKeys.PricingComponentPublishDialogText}
      />
      <PricingPublishResultDialog />
    </Page>
  );
};
